import { adaloBackendAxios } from 'utils/io/http/axios'

const FETCH_REGIONAL_DISCOUNT_START = 'FETCH_REGIONAL_DISCOUNT_START'
const FETCH_REGIONAL_DISCOUNT_SUCCESS = 'FETCH_REGIONAL_DISCOUNT_SUCCESS'
const FETCH_REGIONAL_DISCOUNT_ERROR = 'FETCH_REGIONAL_DISCOUNT_ERROR'
const RESET_REGIONAL_DISCOUNT = 'RESET_REGIONAL_DISCOUNT'

const INITIAL_STATE = {
  loaded: false,
  loading: false,
  couponApplied: false,
  couponId: null,
  percentOff: null,
  amountOff: null,
  countryCode: null,
  name: null,
  error: null,
}

const SESSION_KEY = 'adalo_regional_discount_data'

const loadFromSession = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.warn('Failed to load regional discount from session:', error)
  }

  return null
}

// Save to session storage
const saveToSession = state => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  } catch (error) {
    console.warn('Failed to save regional discount to session:', error)
  }
}

// Reducer
export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_REGIONAL_DISCOUNT_START:
      return {
        ...state,
        loading: true,
        error: null,
      }

    case FETCH_REGIONAL_DISCOUNT_SUCCESS: {
      const newState = {
        ...state,
        loading: false,
        loaded: true,
        error: null,
        ...action.payload,
      }
      saveToSession(newState)

      return newState
    }

    case FETCH_REGIONAL_DISCOUNT_ERROR:
      return {
        ...state,
        loading: false,
        loaded: true,
        error: action.payload,
        couponApplied: false,
      }

    case RESET_REGIONAL_DISCOUNT:
      try {
        sessionStorage.removeItem(SESSION_KEY)
      } catch (error) {
        console.warn('Failed to clear regional discount from session:', error)
      }

      return INITIAL_STATE

    default:
      return state
  }
}

// Actions
const performRegionalDiscountApiCall = async dispatch => {
  dispatch({ type: FETCH_REGIONAL_DISCOUNT_START })

  try {
    const { data } = await adaloBackendAxios.get(
      '/organizations/country-coupon'
    )
    dispatch({ type: FETCH_REGIONAL_DISCOUNT_SUCCESS, payload: data })
  } catch (error) {
    console.error('Failed to fetch regional discount:', error)
    dispatch({ type: FETCH_REGIONAL_DISCOUNT_ERROR, payload: error.message })
  }
}

export const fetchRegionalDiscount =
  (hasRegionalDiscounts = false) =>
  async (dispatch, getState) => {
    const { regionalDiscount } = getState()

    // Check if the feature flag is enabled
    if (!hasRegionalDiscounts) {
      return
    }

    if (process.env.NODE_ENV === 'development') {
      return performRegionalDiscountApiCall(dispatch)
    }

    if (regionalDiscount.loaded || regionalDiscount.loading) {
      return
    }

    const sessionData = loadFromSession()
    if (sessionData && sessionData.loaded) {
      dispatch({ type: FETCH_REGIONAL_DISCOUNT_SUCCESS, payload: sessionData })

      return
    }

    return performRegionalDiscountApiCall(dispatch)
  }

export const resetRegionalDiscount = () => ({ type: RESET_REGIONAL_DISCOUNT })

// Selectors
export const getRegionalDiscount = state => state.regionalDiscount

export const getRegionalDiscountLoaded = state => state.regionalDiscount.loaded

export const getRegionalDiscountLoading = state =>
  state.regionalDiscount.loading

export const getRegionalCouponApplied = state =>
  state.regionalDiscount.couponApplied

export const calculateRegionalDiscountedPrice = (
  originalPrice,
  regionalDiscount,
  hasRegionalDiscounts = false
) => {
  if (!hasRegionalDiscounts || !regionalDiscount.couponApplied) {
    return originalPrice
  }

  if (regionalDiscount.percentOff) {
    return originalPrice * (1 - regionalDiscount.percentOff / 100)
  }

  if (regionalDiscount.amountOff) {
    return Math.max(0, originalPrice - regionalDiscount.amountOff / 100)
  }

  return originalPrice
}

export const getRegionalDiscountLabel = (
  regionalDiscount,
  hasRegionalDiscounts = false
) => {
  if (!hasRegionalDiscounts || !regionalDiscount.couponApplied) {
    return null
  }

  if (regionalDiscount.percentOff) {
    return `${regionalDiscount.percentOff}% OFF`
  }

  if (regionalDiscount.amountOff) {
    return `$${(regionalDiscount.amountOff / 100).toFixed(2)} OFF`
  }

  return null
}

// Initialize state from session storage
const sessionData = loadFromSession()
if (sessionData) {
  Object.assign(INITIAL_STATE, sessionData)
}
